import {
  getHours,
  getMilliseconds,
  getMinutes,
  getSeconds,
  isAfter,
  isBefore,
  isSameMinute,
  parseISO,
  set,
} from "date-fns";

import { SCHEDULE_ITEM_STATUS } from "components/AppointmentsList/constants/scheduleItemStatus";

export const getScheduleItems = (
  doctor,
  timeSlots,
  rowDate,
  slotDurationMinutes
) => {
  let adjustedTimeSlots = timeSlots;

  if (rowDate) {
    const targetDate = parseISO(rowDate);

    adjustedTimeSlots = timeSlots.map((item) => ({
      ...item,
      start: set(targetDate, {
        hours: getHours(item.start),
        minutes: getMinutes(item.start),
        seconds: getSeconds(item.start),
        milliseconds: getMilliseconds(item.start),
      }),
      end: set(targetDate, {
        hours: getHours(item.end),
        minutes: getMinutes(item.end),
        seconds: getSeconds(item.end),
        milliseconds: getMilliseconds(item.end),
      }),
    }));
  }

  return adjustedTimeSlots
    .flatMap((timeSlot, slotIndex) => {

      // All meetings that start in this slot — normally one, but overbooking
      // (and an availability exception covering booked time) lets several share
      // a start time. Each one becomes its own item so nothing is swallowed:
      // collapsing them into a single item took the first meeting's type, which
      // made an exception starting alongside an appointment disappear entirely.
      // Overlaps are resolved into side-by-side lanes (see assign-overlap-lanes).
      const startingMeetings = doctor.schedule.filter((meeting) =>
        isSameMinute(parseISO(meeting.start), timeSlot.start)
      );

      if (startingMeetings.length > 0) {
        // A meeting can outlast the day's last slot — an "unavailable" block
        // ending 23:55 needs four 15-minute rows where only three are left.
        // Spanning past the grid opens an implicit row, making the column taller
        // than the time axis and clipping the item's bottom edge.
        const remainingSlots = adjustedTimeSlots.length - slotIndex;

        return startingMeetings.map((meeting) => ({
          ...timeSlot,
          slotIndex,
          meeting,
          status: meeting.type,
          rowSpan: Math.min(
            calculateRowSpan(meeting, slotDurationMinutes),
            remainingSlots
          ),
        }));
      }

      const isUndergoingMeeting = doctor.schedule.find((meeting) => {
        const meetingStart = parseISO(meeting.start);
        const meetingEnd = parseISO(meeting.end);
        const timeSlotStart = timeSlot.start;

        const isNotStartingSlot = !isSameMinute(meetingStart, timeSlotStart);
        const isWithinMeetingTime =
          (isAfter(timeSlotStart, meetingStart) ||
            isSameMinute(timeSlotStart, meetingStart)) &&
          isBefore(timeSlotStart, meetingEnd);

        return isNotStartingSlot && isWithinMeetingTime;
      });

      if (isUndergoingMeeting) {
        return null;
      }

      const isBeforeStart = isBefore(
        timeSlot.start,
        parseISO(doctor.practiceHours.start)
      );
      const isAfterEnd = isAfter(
        timeSlot.end,
        parseISO(doctor.practiceHours.end)
      );

      if (isBeforeStart || isAfterEnd) {
        return {
          ...timeSlot,
          slotIndex,
          status: SCHEDULE_ITEM_STATUS.UNAVAILABLE,
          rowSpan: 1,
        };
      }

      const isPassed = isBefore(timeSlot.start, new Date());

      if (isPassed) {
        return {
          ...timeSlot,
          slotIndex,
          status: SCHEDULE_ITEM_STATUS.UNAVAILABLE,
          rowSpan: 1,
        };
      }

      return {
        ...timeSlot,
        slotIndex,
        status: SCHEDULE_ITEM_STATUS.AVAILABLE,
        rowSpan: 1,
      };
    })
    .filter(Boolean);
};

const calculateRowSpan = (meeting, slotDurationMinutes) => {
  const startTime = parseISO(meeting.start);
  const endTime = parseISO(meeting.end);

  const diffInMinutes = (endTime - startTime) / (1000 * 60);

  const roundedMinutes = Math.round(diffInMinutes);

  const slotsNeeded = Math.max(
    1,
    Math.ceil(roundedMinutes / slotDurationMinutes)
  );

  return slotsNeeded;
};

